import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthLayout from "../../components/Layouts/AuthLayout";
import { RouteNames } from "../../constants";
import ErrorViewer from "../../components/ErrorViewer";
import LoadingSpinner from "../../components/LoadingSpinner";
import authService from "../../redux/auth/authService";

const validationSchema = Yup.object().shape({
  userName: Yup.string().email("Invalid email address format").required("Please enter a valid email address"),
  firstName: Yup.string().required("Please enter your first name"),
  surname: Yup.string().required("Please enter your surname"),
  phoneNumber: Yup.string().required("Please enter your phone number").min(10, "Enter a valid mobile number"),
  securityQuestion: Yup.number().required("Please select a security question").min(1, "Please select a security question"),
  answer: Yup.string().required("Please provide an answer for the security question"),
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState<{ id: number; question: string }[]>([]);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      userName: "",
      firstName: "",
      surname: "",
      phoneNumber: "",
      securityQuestion: 0,
      answer: "",
    },
  });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await authService.getPublicSecurityQuestions();
        const items = response?.data?.responseObject || [];
        setQuestions(
          (items || []).map((q: any) => ({
            id: q.id ?? q.Id,
            question: q.question ?? q.Question,
          }))
        );
      } catch {
        toast.error("Unable to load security questions. Please try again.");
      }
    };
    load();
  }, []);

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const response = await authService.register({
        userName: values.userName.trim(),
        firstName: values.firstName.trim(),
        surname: values.surname.trim(),
        phoneNumber: values.phoneNumber.trim(),
        securityQuestion: Number(values.securityQuestion),
        answer: values.answer.trim(),
      });
      toast.success(
        response?.data?.statusMessage ||
          "Your registration was successful. Please check your email for a temporary username and password."
      );
      navigate(RouteNames.LOGIN);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.statusMessage ||
          err?.response?.data?.message ||
          "Unable to complete registration. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="login">
        <div className="login_head">
          <span>Already have an account?</span>
          <Link to={RouteNames.LOGIN} className="ml-4 btn btn-outline">
            Log In
          </Link>
        </div>
        <div className="login_form">
          <h1 className="login_form_head">Register</h1>
          <div className="login_form_text mb-6">Please fill in your information</div>
          <form className="pb-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="input_container">
              <label className="input_title" htmlFor="registerEmail">
                Email Address
              </label>
              <input
                id="registerEmail"
                className="input_field"
                type="email"
                placeholder="Email Address"
                disabled={submitting}
                {...register("userName")}
              />
              {errors.userName && <ErrorViewer message={errors.userName.message as string} />}
            </div>
            <div className="input_container">
              <label className="input_title" htmlFor="registerFirstName">
                First Name
              </label>
              <input
                id="registerFirstName"
                className="input_field"
                type="text"
                placeholder="First Name"
                disabled={submitting}
                {...register("firstName")}
              />
              {errors.firstName && <ErrorViewer message={errors.firstName.message as string} />}
            </div>
            <div className="input_container">
              <label className="input_title" htmlFor="registerSurname">
                Surname
              </label>
              <input
                id="registerSurname"
                className="input_field"
                type="text"
                placeholder="Surname"
                disabled={submitting}
                {...register("surname")}
              />
              {errors.surname && <ErrorViewer message={errors.surname.message as string} />}
            </div>
            <div className="input_container">
              <label className="input_title" htmlFor="registerPhone">
                Mobile Number
              </label>
              <input
                id="registerPhone"
                className="input_field"
                type="tel"
                placeholder="Mobile Number"
                disabled={submitting}
                {...register("phoneNumber")}
              />
              {errors.phoneNumber && <ErrorViewer message={errors.phoneNumber.message as string} />}
            </div>
            <div className="input_container">
              <label className="input_title" htmlFor="registerQuestion">
                Security Question
              </label>
              <select
                id="registerQuestion"
                className="input_field"
                disabled={submitting}
                {...register("securityQuestion", { valueAsNumber: true })}
              >
                <option value={0}>Select Security Question</option>
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.question}
                  </option>
                ))}
              </select>
              {errors.securityQuestion && <ErrorViewer message={errors.securityQuestion.message as string} />}
            </div>
            <div className="input_container">
              <label className="input_title" htmlFor="registerAnswer">
                Answer
              </label>
              <input
                id="registerAnswer"
                className="input_field"
                type="text"
                placeholder="Answer"
                disabled={submitting}
                {...register("answer")}
              />
              {errors.answer && <ErrorViewer message={errors.answer.message as string} />}
            </div>
            <button type="submit" className="btn btn-primary btn-primary-full" disabled={submitting}>
              {submitting ? <LoadingSpinner color={"#305F32"} backgroundColor={""} /> : "SUBMIT"}
            </button>
          </form>
          <div className="text-center">
            <Link to={RouteNames.LOGIN} className="btn btn-text btn-text-full">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
